import { type ServiceStore } from '@restify/packages/composables/useStore'

// Hard page cap: an under-constrained query must not turn into a full-collection sweep.
const MAX_PAGES = 50
// Fetch pages in batches, not one Promise.all burst — otherwise dozens of large
// responses buffer at once and hit mongo simultaneously (thundering herd).
const CONCURRENCY = 5

export default async function useForcePagination<T extends ServiceStore>(
  model: T,
  params: T['Query'],
  response: Awaited<ReturnType<T['find']>>,
): Promise<Awaited<ReturnType<T['find']>>> {
  const limit = response.limit
  const total = response.total
  const count = response.data.length

  if (total <= count) return response

  type ResultData = (typeof response.data)[number]

  const numberOfRequests = Math.ceil((total - count) / limit)

  if (numberOfRequests > MAX_PAGES) {
    const label = (model as { serviceName?: string }).serviceName ?? 'unknown'

    console.error(
      `[useForcePagination] ${label}: ${total} records (${numberOfRequests} pages) ` +
        `exceeds the ${MAX_PAGES}-page cap. Query is likely under-constrained — ` +
        'returning only the first page.',
    )

    return response
  }

  const skips = Array.from(Array(numberOfRequests).keys()).map(
    (index) => limit * (index + 1),
  )
  const allData: ResultData[] = [...response.data]

  for (let i = 0; i < skips.length; i += CONCURRENCY) {
    const batch = await Promise.all(
      skips.slice(i, i + CONCURRENCY).map((skip) =>
        model.find({
          ...params,
          query: { ...params.query, $skip: skip },
        }),
      ),
    )

    batch.forEach((paginationResponse) => {
      allData.push(...paginationResponse.data)
    })
  }

  return {
    ...response,
    data: allData,
    total: allData.length,
  }
}
